// Localização: frontend/src/components/UI/Modal.js (versão 0.1.8)

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion'; // 🛑 IMPORTANDO FRAMER MOTION
import { X } from 'lucide-react';

/**
 * Componente Modal reutilizável e aprimorado.
 * @param {boolean} isOpen - Se o modal deve ser visível.
 * @param {function} onClose - Função a ser chamada para fechar o modal (backdrop/ESC).
 * @param {function} onCloseX - Função opcional a ser chamada quando clicar no X (se não fornecido, usa onClose).
 * @param {string} title - Título exibido no cabeçalho do modal (opcional, pode ser omitido se o children o incluir).
 * @param {React.ReactNode} children - Conteúdo principal do modal.
 * @param {string} size - Define o tamanho do modal ('sm', 'md', 'lg', 'xl', '2xl'). Padrão 'lg'.
 * @param {boolean} hideHeader - Oculta o cabeçalho padrão com título e botão fechar.
 */
const Modal = ({ isOpen, onClose, onCloseX, title, children, size = 'lg', hideHeader = false }) => {
    
    // 1. Lógica para fechar com ESC e desabilitar scroll do body (inalterada)
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);


    // 🛑 NOVO HANDLER PARA GARANTIR QUE APENAS O OVERLAY FECHE O MODAL (inalterado)
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // 2. Definição da largura do modal (inalterada)
    let sizeClass = 'sm:max-w-xl'; 
    if (size === 'sm') sizeClass = 'sm:max-w-sm';
    if (size === 'md') sizeClass = 'sm:max-w-md';
    if (size === 'lg') sizeClass = 'sm:max-w-lg';
    if (size === 'xl') sizeClass = 'sm:max-w-2xl';
    if (size === '2xl') sizeClass = 'sm:max-w-3xl';


    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                // Overlay (Fundo escuro e animado)
                <motion.div 
                    className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 sm:p-0" 
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={handleOverlayClick} 
                    role="dialog"
                    aria-modal="true"
                >
                    {/* Modal Container (Animado) - Container externo com bordas arredondadas */}
                    <motion.div 
                        className={`
                            bg-white rounded-2xl shadow-soft w-full 
                            mx-0 mt-8 mb-4 sm:mx-4 sm:my-0 
                            max-h-[95vh] flex flex-col
                            ${sizeClass}
                        `}
                        initial={{ scale: 0.9, opacity: 0, y: 40 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        transition={{ duration: 0.25, type: "spring", stiffness: 300, damping: 30 }}
                        onClick={(e) => e.stopPropagation()} 
                        aria-labelledby="modal-title"
                    >
                        
                        {!hideHeader && (
                            /* Header do Modal Padrão */
                            <div className="flex-shrink-0 bg-white rounded-t-2xl z-10 flex justify-between items-center p-4 sm:p-6 border-b-[0.5px] border-gray-200 shadow-sm"> 
                                <h3 id="modal-title" className="text-xl font-semibold text-gray-800">
                                    {title || 'Informação'}
                                </h3>
                                <button
                                    onClick={onCloseX || onClose}
                                    className="text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-higeia-accent rounded-full p-1 transition"
                                    aria-label="Fechar modal"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        )}

                        {/* Body do Modal - Container interno com scroll e bordas arredondadas preservadas */}
                        <div className={`
                            flex-1 overflow-y-auto modal-scrollbar
                            ${!hideHeader ? 'p-4 sm:p-6' : 'p-4 sm:p-6 rounded-2xl'}
                            ${hideHeader ? '' : 'rounded-b-2xl'}
                        `}> 
                            {children}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    // Renderizar o modal usando Portal diretamente no body para evitar problemas de overflow
    return typeof document !== 'undefined' 
        ? createPortal(modalContent, document.body)
        : null;
};

export default Modal;